import React, { useEffect, useState } from "react";
import {
  Box,
  Skeleton,
  Pagination,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Popover,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import { getBillingInvoicesAction } from "../../redux/actions";
import MaterialIcons from "../../assests/MaterialIcons/MaterialIcons";
import { menuItemSX } from "../Builder/BuilderConst";
import { DayPicker, DateRange } from "react-day-picker";
import {
  format,
  startOfMonth,
  startOfYear,
  startOfQuarter,
  subDays,
  subMonths,
} from "date-fns";
import "react-day-picker/dist/style.css";
import "./BillingInvoice.scss";

// ── Category chip label & style map ──────────────────────────────────────────
const CATEGORY_CHIP: Record<
  string,
  { label: string; bg: string; color: string }
> = {
  rank_tracker: { label: "Rank Tracker", bg: "#EEF6E2", color: "#4F7E14" }, // green-50 / green-600
  map_checkins: { label: "Map Check-Ins", bg: "#EAF2FE", color: "#1259C4" }, // blue-50  / blue-600
  scan_credits: { label: "Scan top-up", bg: "#FFF6E5", color: "#CC7E00" }, // amber-50 / amber-600
  ai_credits_topup: { label: "AI top-up", bg: "#EAEDF1", color: "#354051" }, // slate-100 / slate-700
};

const getCategoryChip = (category: string) => {
  const meta = CATEGORY_CHIP[category?.toLowerCase()] ?? {
    label: category
      ? category.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : null,
    bg: "#f1f5f9",
    color: "#64748b",
  };
  if (!meta.label) return null;
  return (
    <span
      style={{
        backgroundColor: meta.bg,
        color: meta.color,
        padding: "2px 9px",
        borderRadius: "9999px",
        fontSize: "12px",
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        whiteSpace: "nowrap",
        fontFamily: "var(--font-family-manrope)",
        lineHeight: "1.5",
      }}
    >
      {meta.label}
    </span>
  );
};

// ── Status badge (same colours as LocationsModal badge-status) ────────────────
const getStatusBadge = (status: string) => {
  const s = (status || "paid").toLowerCase().trim();

  let className = "badge-status paid";
  let label = s.charAt(0).toUpperCase() + s.slice(1);

  if (s === "paid" || s === "successful" || s === "succeeded") {
    className = "badge-status paid";
    label = "Paid";
  } else if (s === "active") {
    className = "badge-status active";
    label = "Active";
  } else if (s === "trialing" || s === "trial") {
    className = "badge-status trialing";
    label = "Trialing";
  } else if (s === "pending" || s === "open") {
    className = "badge-status trialing"; // blue — same as trialing
    label = s.charAt(0).toUpperCase() + s.slice(1);
  } else if (
    s === "failed" ||
    s === "unpaid" ||
    s === "cancelled" ||
    s === "rejected"
  ) {
    className = "badge-status failed";
    label = "Failed";
  } else if (s === "canceled" || s === "inactive") {
    className = "badge-status canceled";
    label = s.charAt(0).toUpperCase() + s.slice(1);
  }

  return <span className={className}>{label}</span>;
};

// ── Select filter style helper (matches BusinessDashboardPage) ────────────────
const getFilterSelectSx = (isActive: boolean) => ({
  fontSize: "14px",
  borderRadius: "var(--small-border-radius)",
  backgroundColor: "white",
  textTransform: "capitalize",
  fontFamily: "var(--font-family-manrope)",
  height: "30px",
  bgcolor: isActive ? "#e3f2fd !important" : "#ffffff",
  color: isActive ? "#000000 !important" : " #7F7F7F !important",

  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: isActive ? "#1A73E8 !important" : " #d8d8d8 !important",
    border: "1px solid",
  },

  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1A73E8 !important",
  },
  "&:hover": { color: "#1A73E8" },

  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1A73E8 !important",
  },
  "&.Mui-focused": { color: "#1A73E8" },

  "&:active .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1A73E8 !important",
  },
  "&:active": { color: "#1A73E8" },

  "&.selected .MuiOutlinedInput-notchedOutline": {
    borderColor: "#1A73E8 !important",
  },
  "&.selected": { color: "#1A73E8 !important" },

  "@media (max-width:480px)": { width: "100%" },
});

const filterMenuProps = {
  PaperProps: {
    style: {
      boxShadow: "none",
      border: "1px solid #d8d8d8",
      marginTop: "8px",
      borderRadius: "var(--small-border-radius)",
    },
  },
};

const BillingInvoice = () => {
  const { billingInvoices, billingInvoicesLoading, billingInvoicesPagination } =
    useSelector((state: RootState) => state.billing);

  const dispatch = useDispatch<AppDispatch>();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });

  // ── Filters ──────────────────────────────────────────────────────────────────
  const [categoryFilter, setCategoryFilter] = useState<string>("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [range, setRange] = useState<DateRange | undefined>({
    from: undefined,
    to: undefined,
  });
  const [activeShortcut, setActiveShortcut] = useState<string>("");
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const today = new Date();

  useEffect(() => {
    const payload: any = { page, limit: rowsPerPage };
    if (sortConfig.key) {
      payload.sort_by = sortConfig.key;
      payload.sort_order = sortConfig.direction;
    }
    if (categoryFilter) {
      payload.category_filter = categoryFilter;
    }
    if (startDate) {
      payload.date_from = format(new Date(startDate), "yyyy-MM-dd");
    }
    if (endDate) {
      payload.date_to = format(new Date(endDate), "yyyy-MM-dd");
    }
    dispatch(getBillingInvoicesAction(payload));
  }, [
    dispatch,
    page,
    rowsPerPage,
    sortConfig,
    categoryFilter,
    startDate,
    endDate,
  ]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) =>
    setPage(value);

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(event?.target?.value as number);
    setPage(1);
  };

  const handleSort = (key: string) => {
    setSortConfig((prev) => {
      const direction =
        prev.key === key && prev.direction === "asc" ? "desc" : "asc";
      return { key, direction };
    });
    setPage(1);
  };

  const getSortArrow = (key: string) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? (
      <MaterialIcons.ArrowDropUp
        className="arrow-up-animation"
        style={{ width: "20px" }}
      />
    ) : (
      <MaterialIcons.ArrowDropUp
        className="arrow-down-animation"
        style={{ width: "20px" }}
      />
    );
  };

  // ── Calendar helpers ─────────────────────────────────────────────────────────
  const openCalendar = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const closeCalendar = () => setAnchorEl(null);

  const handleStartDateChange = (value: DateRange | undefined) => {
    setStartDate(value?.from ?? null);
    setEndDate(value?.to ?? null);
  };

  const applyShortcut = (type: string) => {
    setActiveShortcut(type);
    let newRange: DateRange;

    switch (type) {
      case "today":
        newRange = { from: today, to: today };
        break;
      case "last7":
        newRange = { from: subDays(today, 6), to: today };
        break;
      case "last4w":
        newRange = { from: subDays(today, 27), to: today };
        break;
      case "last6m":
        newRange = { from: subMonths(today, 6), to: today };
        break;
      case "last12m":
        newRange = { from: subMonths(today, 12), to: today };
        break;
      case "mtd":
        newRange = { from: startOfMonth(today), to: today };
        break;
      case "qtd":
        newRange = { from: startOfQuarter(today), to: today };
        break;
      case "ytd":
        newRange = { from: startOfYear(today), to: today };
        break;
      case "all":
        newRange = { from: undefined, to: today };
        break;
      default:
        return;
    }

    handleStartDateChange(newRange);
    setRange(newRange);
    closeCalendar();
    setPage(1);
  };

  const display =
    activeShortcut === "all"
      ? "All time"
      : startDate && endDate
        ? `${format(startDate, "MMM dd, yyyy")} - ${format(endDate, "MMM dd, yyyy")}`
        : startDate
          ? `${format(startDate, "MMM dd, yyyy")} -`
          : endDate
            ? `${format(endDate, "MMM dd, yyyy")} -`
            : "";

  const formatAmount = (amount: number) => {
    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `$${amount || 0}`;
    }
  };

  const safeInvoices = Array.isArray(billingInvoices) ? billingInvoices : [];
  const totalCount = billingInvoicesPagination?.totalItems ?? 0;
  const totalPages = billingInvoicesPagination?.totalPages ?? 1;

  return (
    <Box className="billing-invoice-container">
      <Box className="billing-card">
        {/* ── Card header ──────────────────────────────────────────────── */}
        <Box className="table-card-header">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Box>
              <h3 className="table-title">Invoices</h3>
              <p className="table-subtitle">
                Every charge in one place — locations, plan and top-ups.
              </p>
            </Box>

            {/* ── Filters row ───────────────────────────────────────── */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              {/* Category Filter */}
              <Select
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                displayEmpty
                MenuProps={filterMenuProps}
                sx={getFilterSelectSx(categoryFilter !== "")}
              >
                <MenuItem
                  sx={{
                    ...menuItemSX,
                    fontSize: "14px",
                    fontFamily: "var(--font-family-manrope)",
                  }}
                  value=""
                >
                  All Types
                </MenuItem>
                {Object.entries(CATEGORY_CHIP).map(([key, meta]) => (
                  <MenuItem
                    key={key}
                    sx={{
                      ...menuItemSX,
                      fontSize: "14px",
                      fontFamily: "var(--font-family-manrope)",
                    }}
                    value={key}
                  >
                    {meta.label}
                  </MenuItem>
                ))}
              </Select>

              {/* Date Range Filter */}
              <TextField
                value={display}
                placeholder="Select Date Range"
                onClick={openCalendar}
                InputProps={{ readOnly: true }}
                inputProps={{ size: display === "All time" ? 8 : undefined }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "30px",
                    width:
                      display === "All time"
                        ? "fit-content"
                        : display
                          ? "230px"
                          : "150px",
                    borderRadius: "var(--small-border-radius)",
                    bgcolor: display !== "" ? "#e3f2fd" : "#ffffff",
                    cursor: "pointer",

                    "& fieldset": {
                      border: "1px solid",
                      borderColor: display !== "" ? "#1A73E8" : "#d8d8d8",
                    },

                    "&:hover fieldset": {
                      borderColor: display !== "" ? "#1A73E8" : "#bfbfbf",
                    },

                    "&.Mui-focused fieldset": {
                      borderColor: "#1A73E8",
                    },
                  },

                  "& .MuiOutlinedInput-input": {
                    height: "30px",
                    width: display === "All time" ? "fit-content" : "100%",
                    fieldSizing: "content",
                    color: display !== "" ? "#000000" : "#7F7F7F",
                    fontSize: "14px",
                    cursor: "pointer",
                    fontFamily: "var(--font-family-manrope)",
                  },

                  "& .MuiInputBase-input::placeholder": {
                    color: "#7F7F7F",
                    opacity: 1,
                    fontSize: "14px",
                    fontFamily: "var(--font-family-manrope)",
                  },
                }}
              />

              {/* CALENDAR POPOVER */}
              <Popover
                elevation={0}
                open={!!anchorEl}
                anchorEl={anchorEl}
                onClose={closeCalendar}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                slotProps={{
                  paper: {
                    sx: {
                      borderRadius: "var(--medium-border-radius)",
                      border: "1px solid #d8d8d8",
                      mt: 1,
                    },
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: {
                      xs: "column",
                      md: "row",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: { xs: "100%", md: 160 },
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    <List
                      sx={{
                        width: {
                          xs: "100%",
                          md: 160,
                        },
                        borderRight: {
                          xs: "none",
                          md: "1px solid #e0e0e0",
                        },
                        display: "flex",
                        flexDirection: {
                          xs: "row",
                          md: "column",
                        },
                        overflowX: {
                          xs: "auto",
                          md: "visible",
                        },
                        whiteSpace: {
                          xs: "nowrap",
                          md: "normal",
                        },
                        gap: {
                          xs: 1.5,
                          md: 0,
                        },
                        padding: "8px 6px",

                        scrollBehavior: "smooth",

                        "&::-webkit-scrollbar": {
                          height: "6px",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: "#bfbfbf",
                          borderRadius: 4,
                        },
                      }}
                    >
                      {[
                        { label: "Today", key: "today" },
                        { label: "Last 7 days", key: "last7" },
                        { label: "Last 4 weeks", key: "last4w" },
                        { label: "Last 6 months", key: "last6m" },
                        { label: "Last 12 months", key: "last12m" },
                        { label: "Month to date", key: "mtd" },
                        { label: "Quarter to date", key: "qtd" },
                        { label: "Year to date", key: "ytd" },
                        { label: "All time", key: "all" },
                      ].map(({ label, key }) => (
                        <ListItemButton
                          key={key}
                          selected={activeShortcut === key}
                          sx={{
                            ...menuItemSX,
                            flexShrink: 0,
                            "&.Mui-selected": {
                              backgroundColor: "#E8F0FE !important",
                              color: "#1a73e8 !important",
                              fontWeight: 400,
                              "& .MuiTypography-root": {
                                color: "#1a73e8 !important",
                                fontWeight: "400 !important",
                              },
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "#E8F0FE !important",
                              "& .MuiTypography-root": {
                                color: "#1a73e8 !important",
                                fontWeight: "400 !important",
                              },
                            },
                          }}
                          onClick={() => applyShortcut(key)}
                        >
                          <ListItemText
                            primary={label}
                            primaryTypographyProps={{
                              fontSize: "14px",
                              fontFamily: "var(--font-family-manrope)",
                              fontWeight: 400,
                              color: activeShortcut === key ? "#1a73e8" : "#374151",
                            }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Box>
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      minHeight: 360,
                      "& .rdp-day_button": {
                        fontWeight: 400,
                        fontSize: "14px",
                      },
                      "& .rdp-selected .rdp-day_button": {
                        fontWeight: 400,
                        fontSize: "14px",
                      },
                      "& .rdp-range_start .rdp-day_button": {
                        fontWeight: 400,
                        fontSize: "14px",
                      },
                      "& .rdp-range_end .rdp-day_button": {
                        fontWeight: 400,
                        fontSize: "14px",
                      },
                      "& .rdp-range_middle .rdp-day_button": {
                        fontWeight: 400,
                        fontSize: "14px",
                      },
                    }}
                  >
                    <DayPicker
                      mode="range"
                      numberOfMonths={2}
                      fixedWeeks
                      selected={range}
                      disabled={{ after: new Date() }}
                      onSelect={(value) => {
                        setActiveShortcut("");
                        setRange(value);
                      }}
                    />
                    <Box
                      display="flex"
                      justifyContent="flex-end"
                      mt={1}
                      gap={1}
                    >
                      <Button
                        variant="outlined"
                        sx={{
                          textTransform: "none",
                          fontSize: "14px",
                          fontFamily: "var(--font-family-manrope)",
                          fontWeight: 600,
                          borderRadius:
                            "var(--action-button-radius, 8px) !important",
                          borderColor: "#E0E0E0 !important",
                          backgroundColor: "#ffffff !important",
                          color: "#374151 !important",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            backgroundColor: "#ffffff !important",
                            borderColor: "#E0E0E0 !important",
                            opacity: 0.9,
                          },
                          "&.Mui-disabled": {
                            opacity: 0.6,
                            cursor: "not-allowed",
                          },
                        }}
                        onClick={() => {
                          setRange({ from: undefined, to: undefined });
                          setStartDate(null);
                          setEndDate(null);
                          setActiveShortcut("");
                          closeCalendar();
                          setPage(1);
                        }}
                      >
                        Clear
                      </Button>

                      <Button
                        variant="contained"
                        disableElevation
                        sx={{
                          textTransform: "none",
                          fontSize: "14px",
                          fontFamily: "var(--font-family-manrope)",
                          fontWeight: 600,
                          borderRadius:
                            "var(--action-button-radius, 8px) !important",
                          backgroundColor:
                            "var(--action-button-background-color, #1a73e8) !important",
                          color: "#ffffff !important",
                          boxShadow: "none !important",
                          transition: "all 0.2s ease-in-out",

                          "&:hover": {
                            boxShadow: "none",
                          },
                          "&.Mui-disabled": {
                            opacity: 0.6,
                            cursor: "not-allowed",
                          },
                        }}
                        onClick={() => {
                          handleStartDateChange(range);
                          if (range?.from && range?.to) closeCalendar();
                          setPage(1);
                        }}
                      >
                        Apply
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Popover>
            </Box>
          </Box>
        </Box>

        {/* ── Table ────────────────────────────────────────────────────── */}
        <Box className="billing-table-wrapper" mt={1}>
          <table className="business-table">
            <thead>
              <tr>
                {/* Date */}
                <th
                  className={`sortable-header${sortConfig.key === "createdAt" ? " active" : ""}`}
                  style={{ width: "14%" }}
                  onClick={() => handleSort("createdAt")}
                >
                  Date {getSortArrow("createdAt")}
                </th>

                {/* Description (name + object + category chip) */}
                <th style={{ width: "42%" }}>Description</th>

                {/* Amount */}
                <th
                  className={`sortable-header${sortConfig.key === "amount" ? " active" : ""}`}
                  style={{ width: "13%" }}
                  onClick={() => handleSort("amount")}
                >
                  Amount {getSortArrow("amount")}
                </th>

                {/* Status */}
                <th
                  className={`sortable-header${sortConfig.key === "state" ? " active" : ""}`}
                  style={{ width: "14%" }}
                  onClick={() => handleSort("state")}
                >
                  Status {getSortArrow("state")}
                </th>

                {/* View action */}
                <th style={{ width: "8%" }} />
              </tr>
            </thead>

            <tbody>
              {billingInvoicesLoading ? (
                /* ── Skeleton rows ─────────────────────────────────────── */
                Array.from(new Array(3)).map((_, idx) => (
                  <tr key={idx}>
                    <td>
                      <Skeleton variant="text" width={90} height={20} />
                    </td>
                    <td>
                      <Skeleton variant="text" width={180} height={20} />
                      <Skeleton
                        variant="rectangular"
                        width={90}
                        height={22}
                        sx={{ borderRadius: "9999px", mt: 0.5 }}
                      />
                    </td>
                    <td>
                      <Skeleton variant="text" width={55} height={20} />
                    </td>
                    <td>
                      <Skeleton
                        variant="rectangular"
                        width={55}
                        height={24}
                        sx={{ borderRadius: "9999px" }}
                      />
                    </td>
                    <td>
                      <Skeleton variant="text" width={30} height={20} />
                    </td>
                  </tr>
                ))
              ) : safeInvoices.length > 0 ? (
                /* ── Data rows ─────────────────────────────────────────── */
                safeInvoices.map((invoice: any, index: number) => (
                  <tr key={index}>
                    {/* Date */}
                    <td>
                      <span
                        style={{
                          fontWeight: 500,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {new Date(invoice.date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "2-digit",
                          year: "numeric",
                        })}
                      </span>
                    </td>

                    {/* Description */}
                    <td style={{ maxWidth: "340px" }}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                          maxWidth: "100%",
                        }}
                      >
                        {/* Name + category chip on same row */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            maxWidth: "100%",
                          }}
                        >
                          <span
                            style={{
                              fontWeight: 500,
                              color: "#0f172a",
                              fontSize: "14px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "260px",
                              display: "inline-block",
                            }}
                            title={invoice.name}
                          >
                            {invoice.name}
                          </span>
                          {invoice.category &&
                            getCategoryChip(invoice.category)}
                        </Box>

                        {/* Object sub-text below */}
                        {invoice.object && (
                          <span
                            style={{
                              color: "#64748b",
                              fontSize: "12px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "260px",
                              display: "inline-block",
                            }}
                            title={invoice.object}
                          >
                            {invoice.object}
                          </span>
                        )}
                      </Box>
                    </td>

                    {/* Amount */}
                    <td>
                      <span
                        style={{
                          fontWeight: 600,
                          color: "#0f172a",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {formatAmount(invoice.amount)}
                      </span>
                    </td>

                    {/* Status */}
                    <td>{getStatusBadge(invoice.status)}</td>

                    {/* View */}
                    <td>
                      {invoice?.url ? (
                        <span
                          className="table-view-link"
                          onClick={() =>
                            window.open(
                              invoice.url,
                              "_blank",
                              "noopener,noreferrer",
                            )
                          }
                        >
                          View
                        </span>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>—</span>
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                /* ── Empty state ───────────────────────────────────────── */
                <tr>
                  <td
                    colSpan={5}
                    style={{
                      textAlign: "center",
                      padding: "40px",
                      color: "#9ca3af",
                    }}
                  >
                    No invoices found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        {/* ── Pagination footer ─────────────────────────────────────────── */}
        <Box className="table-footer">
          <Box className="pagination">
            <span>
              {totalCount > 0 ? (
                <>
                  {(page - 1) * rowsPerPage + 1}
                  {String.fromCharCode(8211)}
                  {Math.min(page * rowsPerPage, totalCount)} of {totalCount}{" "}
                  results shown
                </>
              ) : (
                "No results shown"
              )}
            </span>

            <Box className="page-controls">
              <Pagination
                count={totalPages || 1}
                page={page}
                onChange={handlePageChange}
                color="primary"
                size="small"
              />
              <Select
                value={rowsPerPage}
                onChange={handleRowsPerPageChange}
                size="small"
                sx={{
                  ml: 2,
                  overflow: "hidden",
                  "& .MuiInputBase-root": {
                    backgroundColor: "#e3f2fd !important",
                    borderRadius: "var(--small-border-radius, 8px)",
                    fontWeight: 400,
                    backgroundClip: "padding-box !important",
                  },
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: "#e3f2fd !important",
                    borderRadius: "var(--small-border-radius, 8px)",
                    backgroundClip: "padding-box !important",
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderRadius: "var(--small-border-radius, 8px)",
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      mt: 1,
                      boxShadow: "none !important",
                      "--Paper-shadow": "none !important",
                      borderRadius: "var(--small-border-radius, 8px)",
                      border: "1px solid #d8d8d8",
                      p: 0.5,
                    },
                  },
                }}
              >
                {[5, 10, 20].map((val) => (
                  <MenuItem key={val} value={val} sx={menuItemSX}>
                    {val} / page
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BillingInvoice;
